import { MediaFile } from '@nextforce/api'
import { t } from 'app/i18l'
import { ImageManipulator, SaveFormat } from 'expo-image-manipulator'
import * as ImagePicker from 'expo-image-picker'
import { isGifType } from 'file_folder_repository/util'
import { Alert, Platform } from 'react-native'
import { getMimeType } from './mime'
import { convertHeicToJpegWeb } from './utils'
import ImageCropPicker from './wrappers/react-native-image-crop-picker'

interface PickImageOptions {
  maxVideoDuration?: number
  allowsMultipleSelection?: boolean
}

export type MediaResult =
  | {
      status: 'denied' | 'cancelled'
    }
  | {
      status: 'success'
      files: MediaFile[]
    }

export async function pickImageFromLibrary({
  maxVideoDuration,
  allowsMultipleSelection = false,
}: PickImageOptions = {}): Promise<MediaResult> {
  const permissions = await ImagePicker.getMediaLibraryPermissionsAsync()

  if (!permissions.granted) {
    if (permissions.status === 'denied' && !permissions.canAskAgain) {
      Alert.alert(
        t('imageLibraryPermissionDeniedTitle'),
        t('imageLibraryPermissionDeniedBody')
      )
      return { status: 'denied' }
    }

    const requestedPermissions =
      await ImagePicker.requestMediaLibraryPermissionsAsync()

    if (!requestedPermissions.granted) {
      // Fallback for Android <= 32: proceed without blocking when status remains undetermined
      if (
        Platform.OS === 'android' &&
        typeof Platform.Version === 'number' &&
        Platform.Version <= 32 &&
        requestedPermissions.status ===
          ImagePicker.PermissionStatus.UNDETERMINED &&
        requestedPermissions.canAskAgain === false
      ) {
        // Proceed to system picker which often doesn't require runtime storage permission
        // Any failure will be handled below when launching the picker
      } else {
        return { status: 'denied' }
      }
    }
  }

  let result: ImagePicker.ImagePickerResult
  try {
    result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: maxVideoDuration
        ? ['images', 'videos', 'livePhotos']
        : ['images'],
      allowsEditing: false,
      quality: 1.0,
      orderedSelection: true,
      allowsMultipleSelection,
      selectionLimit: 0,
      videoMaxDuration: maxVideoDuration,
    })
  } catch (error: any) {
    Alert.alert(
      t('imageLibraryPermissionDeniedTitle'),
      t('imageLibraryPermissionDeniedBody')
    )
    return { status: 'denied' }
  }

  if (result.canceled) {
    return { status: 'cancelled' }
  }

  const assets = result.assets
  const files: MediaFile[] = []
  for (const asset of assets) {
    if (
      asset.duration &&
      maxVideoDuration &&
      asset.duration > maxVideoDuration * 1000
    ) {
      Alert.alert(
        'This video is too long to upload.',
        'Please choose a shorter one'
      )
      continue
    }
    const { uri, mimeType } = await convertHeicToJpegIfNeeded({
      uri: asset.uri,
      mimeType: asset.mimeType,
      name: asset.fileName ?? undefined,
    })
    files.push({
      ...asset,
      uri,
      type: 'media',
      mimeType,
    })
  }
  return {
    status: 'success',
    files,
  }
}

export async function takePhotoWithCamera(): Promise<MediaResult> {
  const permissions = await ImagePicker.getCameraPermissionsAsync()

  if (!permissions.granted) {
    if (permissions.status === 'denied' && !permissions.canAskAgain) {
      Alert.alert(
        t('cameraPermissionDeniedTitle'),
        t('cameraPermissionDeniedBody')
      )
      return { status: 'denied' }
    }

    const requestedPermissions =
      await ImagePicker.requestCameraPermissionsAsync()

    if (!requestedPermissions.granted) {
      return { status: 'denied' }
    }
  }

  const result = await ImagePicker.launchCameraAsync({
    mediaTypes: ['images'],
    allowsEditing: false,
    orderedSelection: true,
    allowsMultipleSelection: true,
    selectionLimit: 0,
    quality: 1,
  })

  if (result.canceled) {
    return { status: 'cancelled' }
  }

  const assets = result.assets
  const files: MediaFile[] = []
  for (const asset of assets) {
    const { uri, mimeType } = await convertHeicToJpegIfNeeded({
      uri: asset.uri,
      mimeType: asset.mimeType,
      name: asset.fileName ?? undefined,
    })
    files.push({
      ...asset,
      uri,
      type: 'media',
      mimeType,
    })
  }
  return {
    status: 'success',
    files,
  }
}

export async function editImage(
  asset: ImagePicker.ImagePickerAsset
): Promise<MediaFile> {
  if (
    asset.type === 'image' &&
    !isGifType(getMimeType(asset.uri)) &&
    Platform.OS !== 'web'
  ) {
    const image = await ImageCropPicker.openCropper({
      path: asset.uri,
      mediaType: 'photo',
      freeStyleCropEnabled: true,
      ...(Platform.OS === 'ios'
        ? { width: asset.width, height: asset.height }
        : {}),
    }).catch(error => {
      return undefined
    })
    if (image) {
      asset.height = image.height
      asset.width = image.width
      asset.uri = image.path
    }
  }
  return {
    ...asset,
    type: 'media',
    mimeType:
      (Platform.OS === 'web'
        ? asset.mimeType || getMimeType(asset.uri)
        : asset.mimeType) ?? 'application/octet-stream',
  }
}

/**
 * Converts a HEIC/HEIF image to JPEG when needed so it can be uploaded to
 * backends that don't accept HEIC/HEIF. Works for both image-picker assets and
 * files selected through the document picker. Non-HEIC files are returned
 * unchanged.
 */
export async function convertHeicToJpegIfNeeded({
  uri,
  mimeType,
  name,
}: {
  uri: string
  mimeType?: string
  name?: string
}): Promise<{ uri: string; mimeType: string; name?: string }> {
  let resolvedUri = uri
  let resolvedName = name
  // On web, getMimeType relies on file extension which doesn't work for blob: URLs.
  // Prefer the picker's mimeType when available, fall back to the file name/uri.
  let resolvedMimeType =
    Platform.OS === 'web' ? mimeType || getMimeType(name ?? uri) : mimeType

  const isHeicMimeType =
    !!resolvedMimeType &&
    (resolvedMimeType.includes('heic') || resolvedMimeType.includes('heif'))
  const isHeicExtension =
    /\.hei[cf]$/i.test(name ?? '') || /\.hei[cf]$/i.test(uri)
  const isHeic = isHeicMimeType || isHeicExtension

  if (Platform.OS === 'web') {
    // The document picker on web returns blob: URLs and the browser often
    // reports HEIC/HEIF as an empty or generic mime type, so rely on the
    // file extension too. convertHeicToJpegWeb handles data:, blob: and
    // http(s): URIs and safely no-ops (returns null) for non-HEIC content.
    if (isHeic || resolvedMimeType === 'application/octet-stream') {
      const converted = await convertHeicToJpegWeb(
        uri,
        resolvedMimeType || 'image/heic'
      )
      if (converted) {
        resolvedUri = converted.uri
        resolvedMimeType = converted.mimeType
        resolvedName = replaceHeicExtension(name)
      }
    }
  } else if (isHeic) {
    try {
      const context = ImageManipulator.manipulate(uri)
      const imageRef = await context.renderAsync()
      const saved = await imageRef.saveAsync({
        format: SaveFormat.JPEG,
        compress: 1,
      })
      resolvedUri = saved.uri
      resolvedMimeType = 'image/jpeg'
      resolvedName = replaceHeicExtension(name)
    } catch (e) {
      console.error('[ImagePicker] Conversion to JPEG failed:', e)
      // fallback to original if conversion fails
    }
  }

  return {
    uri: resolvedUri,
    mimeType: resolvedMimeType ?? 'application/octet-stream',
    name: resolvedName,
  }
}

function replaceHeicExtension(name?: string): string | undefined {
  if (!name) {
    return name
  }
  return name.replace(/\.hei[cf]$/i, '.jpg')
}
